import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import { Autocomplete, Button, Chip, MenuItem, SelectChangeEvent, Stack, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { ChangeEvent, forwardRef, KeyboardEvent, useEffect, useMemo, useState } from "react";
import { throttle } from "throttle-debounce";
import Modal, { CommonModalProps } from "..";
import { fileService, userService } from "../../../Core/Api/services";
import { FileTransferInitiator } from "../../../Core/Api/Types/file-transfer-initiator";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { DeleteForm, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import FormInputEmail from "../../Forms/FormInputEmail/form-input-email";
import FormInputNumber from "../../Forms/FormInputNumber/form-input-number";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import FormSection from "../../Forms/FormSection/form-section";
import Toast from "../../Misc/Toast/toast";
import { useTranslation } from "react-i18next";

export type ModalRequestDownloadLinkElementProps = {
  fileIds: number[];
};

export type ModalRequestDownloadLinkProps = CommonModalProps<ModalRequestDownloadLinkElementProps>;

// TODO : test all cases of select because sometimes it's not what we want
// TODO : is mailing list selection multiple ? check in actual front, if yes we have to change some things
// TODO : change fileId input prop by an array cause we want to be able to do it for an array
// TODo : rework modal to match figma : https://www.figma.com/file/PfO54Bdq2lH1w3kCo5WqVe/Mediaspot---User-Interface-%5B05%5D?node-id=2356%3A247221
const ModalRequestDownloadLink = forwardRef<any, ModalRequestDownloadLinkProps>((props, ref) => {
  const { t } = useTranslation();
  const { fileIds, closeModal, ...modalProps } = props;

  const formId = "modal-request-download";
  const securitySectionId = `${formId}-validity-security`;
  const notificationSectionId = `${formId}-notifications`;

  const commentInitialValue = "";
  const emailInitialValue = "";
  const validDuringInitialValue = 15;
  // #region states
  // data
  const [fetchedMailingList, setFetchedMailingList] = useState<MailingListDto[]>([]);
  const [fetchedUsersEmails, setFetchedUsersEmails] = useState<string[]>([]);
  // form statuses
  const isSecuritySectionValid = useAppSelector(GetSectionIsValid(formId, securitySectionId));

  // Validity and Security
  const [selectFileTransfer, setSelectFileTransfer] = useState<FileTransferInitiator>(
    FileTransferInitiator.AsperaConnectFasp
  );
  const [inputDaysValue, setInputDaysValue] = useState(15);
  const [inputCommentValue, setInputCommentValue] = useState(commentInitialValue);
  // Notifications
  const [manualEmailValue, setManualEmailValue] = useState(emailInitialValue);
  const [autocompleteMailingListValue, setAutocompleteMailingListValue] = useState<string | null>(null);
  const [autocompleteUsersMailValue, setAutoCompleteUsersMailValue] = useState<string | null>(null);
  const [autocompleteUserMailInput, setAutocompleteUserMailInput] = useState<string>("");
  // #endregion

  // use multiple selection
  const { select, selectList, selected } = useMultipleSelection<string>();

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  // #region functions
  // Validity and Security
  const handleSelectFileTransferChange = (event: SelectChangeEvent) => {
    setSelectFileTransfer(event.target.value as FileTransferInitiator);
  };

  const handleChangeDaysValue = (event: ChangeEvent<HTMLInputElement>) => {
    let value = Number(event.target.value);
    if (value < 0) value = 0;
    if (value > 30) value = 30;
    setInputDaysValue(value);
  };

  const handleCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputCommentValue(value);
  };

  // Notifications
  const handleChangeManualMailingList = (event: any) => {
    const value = event.target.value as string;
    setManualEmailValue(value);
  };

  const handlePressEnterMailingManual = (event: KeyboardEvent<HTMLInputElement>) => {
    // if not Enter pressed
    if (event.key !== "Enter") return;

    select(manualEmailValue);
    setManualEmailValue("");
  };

  const handleAutocompleteMailingListChange = (value: string | null) => {
    setAutocompleteMailingListValue(value);
    if (value === null || value === "") return;

    selectList(fetchedMailingList.find((l) => l.name === value)?.emails || []);
  };

  const throttledUpdateSearchEmail = useMemo(
    () =>
      throttle(
        GlobalConst.SEARCH_THROTTLE_DELAY_MS,
        (searchValue: string) => {
          userService.GetUsersEmailsFromSearh(searchValue).then((emails) => {
            setFetchedUsersEmails(emails);
          });
        },
        {
          noLeading: true,
        }
      ),
    []
  );

  const handleAutocompleteUsersMailInputChange = (searchValue: string) => {
    setAutocompleteUserMailInput(searchValue);

    if (!searchValue || searchValue === "") return;

    throttledUpdateSearchEmail(searchValue);
  };

  const handleAutocompleteUsersMailsValueChange = (value: string | null) => {
    if (value === null) return;
    select(value.split(" ")[0]);
    setAutoCompleteUsersMailValue(null);
    setAutocompleteUserMailInput("");
  };

  // Modal

  const handleCreateDownloadLink = () => {
    fileService
      .CreateTransferDownloadLink({
        fileIds: fileIds,
        expireDays: inputDaysValue,
        notes: inputCommentValue,
        emails: selected,
        initiator: selectFileTransfer,
        noEmail: false,
      })
      .then(() => {
        enqueueSnackbar(t("alert:success.fileDownloadLinkCreated"), {
          autoHideDuration: 6500,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          disableWindowBlurListener: true,
          content: (key, message) => <Toast type="success" title={message?.toString()} />,
        });
        handleCancelClick();
      });
  };

  const handleCancelClick = () => {
    dispatch(DeleteForm({ formId: formId }));
    closeModal();
  };

  // #endregion

  useEffect(() => {
    userService.GetMailingList().then((mailingList) => {
      setFetchedMailingList(mailingList);
    });
  }, []);

  return (
    <Modal
      title={t("modal:title.createDownloadLink")}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={handleCancelClick}>
            {t("common:cancel")}
          </Button>
          <Button
            variant="outlined"
            color="success"
            disabled={!isSecuritySectionValid || selected.length === 0}
            onClick={handleCreateDownloadLink}
          >
            {t("button:createDownloadLink")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <FormSection
          icon={<SecurityIcon />}
          title={t("form:section.title.validityAndSecurity")}
          status={isSecuritySectionValid ? "complete" : "error"}
          statusText={isSecuritySectionValid ? t("common:ok") : t("form:helper.missingConfiguration")}
          helperText={t("form:helper.someFieldsAreMissing")}
          defaultExpanded
        >
          <Stack spacing={2} direction="row">
            <FormInputSelect
              label={t("form:label.transferMethod")}
              startAdornment={<DownloadOutlinedIcon />}
              IconComponent={ExpandMoreRoundedIcon}
              sx={{ mb: 1 }}
              value={selectFileTransfer}
              onChange={handleSelectFileTransferChange}
              defaultValue={FileTransferInitiator.AsperaConnectFasp}
              validationProps={{
                formId: formId,
                formSectionId: securitySectionId,
                fieldId: "select-file-transfer",
                required: true,
                initialState: true,
              }}
              stackProps={{
                flex: 0,
                spacing: 1,
              }}
            >
              <MenuItem value={FileTransferInitiator.Http}>{t("menu:item.httpDownload")}</MenuItem>
              <MenuItem value={FileTransferInitiator.AsperaConnectFasp}>{t("menu:item.asperaConnect")}</MenuItem>
            </FormInputSelect>
            <FormInputNumber
              label={t("field:label.validDuringDays")}
              startAdornment={<CalendarTodayOutlinedIcon />}
              value={inputDaysValue}
              onChange={handleChangeDaysValue}
              validationProps={{
                formId: formId,
                formSectionId: securitySectionId,
                fieldId: "input-during-days",
                required: true,
                minValue: 1,
                maxValue: 31,
                initialState: true,
                initialValue: validDuringInitialValue,
                notDecimal: true,
                positive: true,
              }}
              stackProps={{
                flex: 0,
                spacing: 1,
              }}
            />
            <FormInputText
              label={t("field:label.enterNotesOrComments")}
              startAdornment={<ChatOutlinedIcon />}
              placeholder={t("field:placeholder.enterNotesOrComments")}
              value={inputCommentValue}
              onChange={handleCommentChange}
              validationProps={{
                formId: formId,
                formSectionId: securitySectionId,
                fieldId: "comment-field",
                initialState: false,
                required: true,
                initialValue: commentInitialValue,
                minLength: 10,
              }}
              stackProps={{
                flex: 1,
                spacing: 1,
              }}
            />
          </Stack>
        </FormSection>
        <FormSection
          icon={<NotificationsActiveOutlinedIcon />}
          title={t("common:notifications")}
          status={selected.length > 0 ? "complete" : "error"}
          statusText={selected.length > 0 ? t("common:ok") : t("form:helper.missingConfiguration")}
          helperText={t("form:helper.needOneEmail")}
          defaultExpanded
        >
          <Stack spacing={2}>
            <FormInputEmail
              label={t("field:label.addEmailAddressManually")}
              startAdornment={<EmailIcon />}
              placeholder={t("field:placeholder.enterValidEmail")}
              value={manualEmailValue}
              onChange={handleChangeManualMailingList}
              onKeyDown={handlePressEnterMailingManual}
              validationProps={{
                formId: formId,
                formSectionId: notificationSectionId,
                fieldId: "input-mail",
                required: false,
                initialState: true,
                initialValue: emailInitialValue,
              }}
              stackProps={{
                spacing: 1,
              }}
            />
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value, index) => (
                <Chip key={index} label={value} variant="tag" size="small" onDelete={() => select(value)} />
              ))}
            </Stack>
            <Autocomplete
              popupIcon={<ExpandMoreRoundedIcon />}
              options={fetchedMailingList.map((list) => list.name)}
              value={autocompleteMailingListValue}
              onChange={(event, value) => handleAutocompleteMailingListChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("field:label.loadPreConfiguredMailingList")}
                  placeholder={t("field:placeholder.selectMailingList")}
                />
              )}
            />
            <Autocomplete
              popupIcon={<ExpandMoreRoundedIcon />}
              options={fetchedUsersEmails}
              noOptionsText={t("field:helper.typeNameToFetchEmails")}
              // TODO : is this filter options useful ?
              filterOptions={(x) => x}
              inputValue={autocompleteUserMailInput}
              onInputChange={(event, value) => handleAutocompleteUsersMailInputChange(value)}
              value={autocompleteUsersMailValue}
              onChange={(event, value) => handleAutocompleteUsersMailsValueChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("field:label.searchEmails")}
                  placeholder={t("field:placeholder.searchUserEmail")}
                />
              )}
            />
          </Stack>
        </FormSection>
      </Stack>
    </Modal>
  );
});

export default ModalRequestDownloadLink;
